import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { Button } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";

import ProofDocumentControl from "~/components/Forms/ViolationRecordingForm/ProofDocumentControl";
import RichTextExample from "~/shared/ui/Fields/RichTextExample";
import { NOTIFICATIONS } from "~/constans/notifications";
import { checkSubdivision } from "~/hooks/checkSubdivision";
import { getFormatDate } from "~/libs/getFormatDate";
import { uploadFile } from "~/libs/uploadFile";
import { useAnnouncementGoBack } from "~/pages/Announcement/hooks/useAnnouncementGoBack";
import {
  useAddNotificationMutation,
  useAnnouncementCreateMutation,
  useAnnouncementEditMutation,
} from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

import s from "./AnnouncementForm.less";

export enum AnnouncementImportance {
  Important = "important",
  Urgent = "urgent",
}

type AnnouncementFormData = {
  id?: string;
  title: string;
  singedBy: string;
  date: dayjs.Dayjs | null;
  importance: AnnouncementImportance;
  SubdivisionId?: string | null;
  createdAt?: string;
  file?: string;
};

interface IProps {
  refetch?;
  AnnouncementForm?;
  loading?;
  subdivisions?;
}

const AnnouncementForm: FC<IProps> = ({
  AnnouncementForm = {},
  subdivisions = [],
}) => {
  const { t } = useTranslation();
  const checkSub = checkSubdivision();
  const { params } = useRouteMatch();
  const personalId = params["id"];
  const goBack = useAnnouncementGoBack();

  const [addNotificationMutation] = useAddNotificationMutation({});
  const isAdd = personalId === "add";

  const [fileId, setFileId] = useState([]);
  const [textError, setTextError] = useState(false);
  const [filesError, setFilesError] = useState(false);
  const [uploadFileLoader, setUploadFileLoader] = useState(false);

  const isFiles = fileId.length > 0;

  const notificationError = (message: string) =>
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.error,
        content: message,
      },
    });

  const [setAnnouncementCreateMutation, announcementCreateMutation] =
    useAnnouncementCreateMutation({
      onCompleted() {
        addNotificationMutation({
          variables: {
            type: NOTIFICATIONS.success,
            content: t("common.adding_was_successful"),
          },
        });
        goBack();
      },
      onError(vals) {
        notificationError(vals["message"]);
      },
    });

  const [setAnnouncementEditMutation, announcementEditMutation] =
    useAnnouncementEditMutation({
      onCompleted() {
        addNotificationMutation({
          variables: {
            type: NOTIFICATIONS.success,
            content: t("common.сhange_was_successful"),
          },
        });
        goBack();
      },
      onError(vals) {
        notificationError(vals["message"]);
      },
    });

  const {
    id,
    title,
    importance,
    date,
    text,
    singedBy,
    SubdivisionId,
    Documents = [],
    createdAt,
  } = AnnouncementForm;

  const [textCommand, setTextCommand] = useState(
    text ? (text.indexOf("<p>") >= 0 ? text : `<p>${text}</p>`) : `<p></p>`,
  );

  useEffect(() => {
    if (!isAdd) {
      setFileId(Documents.map((el) => el.id));
    }
  }, [isAdd, AnnouncementForm]);

  const importanceOptions = Object.values(AnnouncementImportance).map(
    (value) => ({ value, label: t(`announcement.${value}`) }),
  );

  const { handleSubmit, control, errors } = useForm<AnnouncementFormData>({
    defaultValues: {
      id: isAdd ? undefined : id,
      title: isAdd ? "" : title,
      singedBy: isAdd ? "" : singedBy,
      date: date ? dayjs(date) : null,
      importance: isAdd ? AnnouncementImportance.Important : importance,
      SubdivisionId: isAdd ? checkSub || null : SubdivisionId || null,
      createdAt: isAdd ? "" : getFormatDate(createdAt, true),
    },
  });

  const onSubmit = (data: AnnouncementFormData) => {
    const isTextEmpty = textCommand === "<p></p>";
    const isFilesEmpty = fileId.length === 0;

    setTextError(isTextEmpty);
    setFilesError(isFilesEmpty);

    if (isTextEmpty || isFilesEmpty) return;

    const variables = {
      importance: data.importance,
      date: data.date ? dayjs(data.date).toDate() : undefined,
      title: data.title,
      text: textCommand.length > 0 ? textCommand : "",
      singedBy: data.singedBy,
      SubdivisionId: data.SubdivisionId || undefined,
      Documents: fileId.length ? fileId : undefined,
    };

    if (isAdd) {
      setAnnouncementCreateMutation({ variables });
    } else {
      setAnnouncementEditMutation({
        variables: { id: data.id, ...variables },
      });
    }
  };

  return (
    <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        {!isAdd && (
          <Field
            type="input"
            name="id"
            control={control}
            label={t("announcement.id")}
            disabled
          />
        )}
        <Field
          type="input"
          name="title"
          control={control}
          label={t("announcement.title")}
          rules={{
            required: t("validation.required"),
            maxLength: { value: 250, message: t("validation.more250") },
          }}
          error={errors["title"]?.message}
        />

        <div>
          <span className={s.fieldLabel}>{t("announcement.text")}</span>
          <RichTextExample
            setTextCommand={setTextCommand}
            textCommand={textCommand}
          />
          {textError && <div className={s.errorText}>{t("q.asterisk")}</div>}
        </div>

        <Field
          type="select"
          name="importance"
          control={control}
          label={t("announcement.importanceForm")}
          options={importanceOptions}
          rules={{ required: t("validation.required") }}
          error={errors["importance"]?.message}
        />

        <Field
          type="datePicker"
          name="date"
          control={control}
          label={t("announcement.date")}
          format="DD.MM.YYYY"
          disabledDate={(current) =>
            current ? current.isBefore(dayjs(), "day") : false
          }
          rules={{ required: t("validation.required") }}
          error={
            (errors["date"] as unknown as { message?: string } | undefined)
              ?.message
          }
        />

        <Field
          type="input"
          name="singedBy"
          control={control}
          label={t("announcement.singedBy")}
          rules={{
            required: t("validation.required"),
            maxLength: { value: 250, message: t("validation.more250") },
          }}
          error={errors["singedBy"]?.message}
        />

        <Field
          type="select"
          name="SubdivisionId"
          control={control}
          label={t("announcement.SubdivisionId")}
          options={subdivisions}
          showSearch
          optionFilterProp="label"
          rules={!checkSub ? { required: t("validation.required") } : undefined}
          error={errors["SubdivisionId"]?.message}
        />

        {!isAdd && (
          <Field
            type="input"
            name="createdAt"
            control={control}
            label={t("announcement.createdAt")}
            disabled
          />
        )}

        <div>
          <span className={s.fieldLabel}>{t("announcement.Documents")}</span>
          <Field
            type="file"
            name="file"
            control={control}
            loading={uploadFileLoader}
            onUpload={async (file) => {
              setUploadFileLoader(true);
              try {
                const uploaded = await uploadFile(file, () =>
                  notificationError(t("common.data_uploaded_error")),
                );
                if (uploaded) {
                  // Файлов может быть несколько: id уходит в общий список,
                  // а onUpload ничего не возвращает — значение поля остаётся
                  // null, и FileField снова показывает кнопку загрузки.
                  setFileId((old) => [...old, uploaded.id]);
                  addNotificationMutation({
                    variables: {
                      type: NOTIFICATIONS.success,
                      content: t("common.file_uploaded_successfully"),
                    },
                  });
                }
              } catch (error) {
                if (error.response?.data?.message === "File too large") {
                  notificationError(t("common.data_uploaded_error_size"));
                } else {
                  notificationError(t("common.data_uploaded_error"));
                }
              } finally {
                setUploadFileLoader(false);
              }
            }}
          />
          {filesError && <div className={s.errorText}>{t("q.asterisk")}</div>}

          {isFiles && (
            <div className={s.filesBlock}>
              <div className={s.filesTitle}>Прикрепленые файлы</div>
              <div className={s.filesList}>
                {fileId.map((id) => (
                  <ProofDocumentControl
                    key={id}
                    filesId={fileId}
                    isFileRow
                    setFilesId={setFileId}
                    id={id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={s.submitRow}>
          <Button
            htmlType="submit"
            size={"medium"}
            loading={
              announcementCreateMutation.loading ||
              announcementEditMutation.loading
            }
          >
            {t("ui.save")}
          </Button>
        </div>
      </Flex>
    </form>
  );
};
export default AnnouncementForm;
